import { ApiError } from '../api/client.ts';
import { isSessionFailure } from './LoadFailure.tsx';
import { lastUpdated } from '../lastUpdated.ts';


/**
 * The bar above every view of a client (Arthur, 21 October, 16:12): the client's name in bold, and the refresh as a
 * two-line button, "Refresh" over "Last Updated h:mm AM/PM", set as a facility's name is over its address. When the
 * last fetch failed it says so in words, as Staleness does: unreachable, an error from the server, or a session that
 * is no longer valid are different facts.
 */
export function ClientBar({ name, clientRef, updatedAt, isFetching, error, onRefresh }: {
  name: string; clientRef: string | null; updatedAt: number; isFetching: boolean; error: Error | null; onRefresh: () => void;
}) {
  const time = lastUpdated(updatedAt);
  const reason = !error
    ? null
    : isSessionFailure(error, { forbiddenIsSession: false })
      ? 'Session not valid'
      : error instanceof ApiError
      ? 'The server reported an error'
      : 'Cannot reach the server';
  return (
    <div className="flex min-h-[56px] flex-wrap items-center gap-x-4 gap-y-1 px-4 py-1.5">
      <p className="min-w-0 flex-1 text-lg">
        <span className="font-bold break-words text-neutral-900">{name}</span>
        {clientRef && <span className="ml-2 text-sm text-neutral-600">{clientRef}</span>}
      </p>
      {reason && (
        <p role="alert" className="text-sm font-semibold text-critical-edge">
          {reason}{time ? `: showing what was there at ${time}` : ''}
        </p>
      )}
      <button type="button" onClick={onRefresh} aria-label={time ? `Refresh, last updated ${time}` : 'Refresh'}
              className="min-h-[44px] rounded-lg px-3 py-1 text-right hover:bg-neutral-50 active:bg-neutral-100">
        <span className="block text-lg font-semibold text-neutral-900">{isFetching ? 'Refreshing…' : 'Refresh'}</span>
        <span className="block text-sm text-neutral-600">{time ? `Last Updated ${time}` : 'Loading…'}</span>
      </button>
    </div>
  );
}
